import React, {useMemo, useState} from 'react'
import {KTSVG} from '../../../../../_websoft/helpers'
import clsx from 'clsx'
import PanelListSection from '../PanelListSection'
import {
  saveProizvodnja,
  updateArhivirajNalog,
  updateIzmjeniStatus,
  updateNalogVracen,
} from '../../../../api/proizvodnja'
import notifyToast from '../../../../components/Toast/Toast'

type Props = {
  rowSelection: any[]
  updatePage: () => void
}

const ActionButton = ({
  onClick,
  disabled,
  loading,
  text,
  iconPath = '/media/icons/duotune/arrows/arr075.svg',
  variant = 'btn-success',
}: {
  onClick: () => void
  disabled?: boolean
  loading?: boolean
  text: string
  iconPath?: string
  variant?: string
}) => {
  return (
    <button
      type='button'
      className={clsx(
        'btn',
        variant,
        'w-100',
        'd-flex',
        'align-items-center',
        'justify-content-center'
      )}
      disabled={disabled || loading}
      onClick={onClick}
    >
      {loading ? (
        <>
          <span
            className='spinner-border spinner-border-sm me-2'
            role='status'
            aria-hidden='true'
          />
          <span>Obrada...</span>
        </>
      ) : (
        <>
          <KTSVG className='svg-icon svg-icon-3 me-2' path={iconPath} />
          <span>{text}</span>
        </>
      )}
    </button>
  )
}

const PrroizodnjaIzmjena: React.FC<Props> = ({rowSelection, updatePage}) => {
  const stavkaList = useMemo(() => rowSelection.map((item) => item.original.id), [rowSelection])

  const [izmjeniStatus, setizmjeniStatus] = useState<string>('')
  const [datum, setDatum] = useState<string>('')
  const [nalogVracen, setNalogVracen] = useState<string>('')
  const [arhivirajNalog, setArhivirajNalog] = useState<string>('')
  const [upisiNalog, setupisiNalog] = useState<string>('')

  const [loading, setLoading] = useState(false)

  const selectedCount = stavkaList.length

  const onClickIzmjeniStatus = () => {
    setLoading(true)
    const params = {stavkaId: stavkaList, status: izmjeniStatus, datum}
    updateIzmjeniStatus(params)
      .then((response) => {
        notifyToast(true, response.message)
        updatePage()
        setizmjeniStatus('')
        setDatum('')
      })
      .catch((response) => notifyToast(false, response.message))
      .finally(() => setLoading(false))
  }

  const onClickNalogVracen = () => {
    setLoading(true)
    const params = {stavkaId: stavkaList, status: nalogVracen}
    updateNalogVracen(params)
      .then((response) => {
        notifyToast(true, response.message)
        updatePage()
        setNalogVracen('')
      })
      .catch((error) => notifyToast(false, error.response?.data?.message ?? 'Greška'))
      .finally(() => setLoading(false))
  }

  const onClickArhivirajNalog = () => {
    setLoading(true)
    const params = {stavkaId: stavkaList, status: arhivirajNalog}
    updateArhivirajNalog(params)
      .then((response) => {
        notifyToast(true, response.message)
        updatePage()
        setArhivirajNalog('')
      })
      .catch((error) => notifyToast(false, error.response?.data?.message ?? 'Greška'))
      .finally(() => setLoading(false))
  }

  const handleUpisiNalog = () => {
    setLoading(true)
    const dataToSend = {brojNaloga: upisiNalog}
    saveProizvodnja(dataToSend)
      .then((response) => {
        // response: { message: string, brojNaloga: string, count: number }
        const msg = response?.message ?? 'Uspješno.'
        const broj = response?.brojNaloga ?? upisiNalog
        const count = Number(response?.count ?? 0)

        if (count > 0) {
          notifyToast(true, `${msg} Nalog: ${broj}. Upisan.`)
          setupisiNalog('')
          updatePage()
        } else {
          // if API says success but imported 0 records (or nothing new)
          notifyToast(false, `${msg} Nalog: ${broj}. Nije uvezen nijedan zapis.`)
        }
      })
      .catch((error: any) => {
        notifyToast(false, error?.response?.data?.message ?? 'Greška')
        console.error('Error in handleUpisiNalog:', error)
      })
      .finally(() => setLoading(false))
  }

  return (
    <PanelListSection
      title='Izmjena'
      description='Upravljajte odabranim stavkama ili upišite novi FMK nalog.'
      icon='bi-pencil-square'
      badge={
        <span className={clsx('badge', selectedCount ? 'badge-light-success' : 'badge-light')}>
          Odabrano: {selectedCount}
        </span>
      }
    >
      <div
        className={clsx(
          'rounded p-4 mb-5 d-flex align-items-center gap-3',
          selectedCount ? 'bg-light-success' : 'bg-light'
        )}
      >
        <i
          className={clsx(
            'bi fs-3',
            selectedCount ? 'bi-check2-square text-success' : 'bi-info-circle text-primary'
          )}
          aria-hidden='true'
        />
        <span className='text-gray-700 fs-7'>
          {selectedCount
            ? 'Promjene statusa i oznaka primjenjuju se na odabrane stavke.'
            : 'Za promjenu statusa i oznaka prvo odaberite stavke u tabeli. Upis novog naloga je dostupan bez odabira.'}
        </span>
      </div>
      <div className='row g-5'>
        <div className='col-12 col-xl-4'>
          <div className='border rounded p-5 h-100 d-flex flex-column'>
            <div className='d-flex align-items-center gap-3 mb-5'>
              <i className='bi bi-arrow-repeat text-primary fs-2' aria-hidden='true' />
              <h4 className='fs-5 fw-bold mb-0'>Promjena statusa</h4>
            </div>
            <div className='mb-4'>
              <label htmlFor='panel-edit-status' className='form-label fw-semibold required'>
                Status
              </label>
              <select
                id='panel-edit-status'
                className='form-select form-select-solid'
                onChange={(e) => setizmjeniStatus(e.target.value)}
                value={izmjeniStatus}
              >
                <option value=''>Odaberi status</option>
                <option value='Zavrseno'>Završeno</option>
                <option value='Planirano'>Proizvodnja planirana</option>
                <option value='Na čekanju'>Na čekanju</option>
              </select>
            </div>
            <div className='mb-5'>
              <label htmlFor='panel-edit-date' className='form-label fw-semibold required'>
                Datum statusa
              </label>
              <input
                id='panel-edit-date'
                type='date'
                className='form-control form-control-solid'
                onChange={(e) => setDatum(e.target.value)}
                value={datum}
              />
            </div>
            <div className='mt-auto'>
              <ActionButton
                text='Potvrdi promjenu'
                onClick={onClickIzmjeniStatus}
                loading={loading}
                disabled={!datum || !izmjeniStatus || !selectedCount}
                variant='btn-primary'
              />
            </div>
          </div>
        </div>
        <div className='col-12 col-xl-4'>
          <div className='border rounded p-5 h-100'>
            <div className='d-flex align-items-center gap-3 mb-5'>
              <i className='bi bi-bookmark-check text-primary fs-2' aria-hidden='true' />
              <h4 className='fs-5 fw-bold mb-0'>Oznake naloga</h4>
            </div>
            <label htmlFor='panel-edit-returned' className='form-label fw-semibold'>
              Nalog vraćen
            </label>
            <div className='row g-3 align-items-end mb-5'>
              <div className='col-12 col-sm-6 col-xl-12 col-xxl-6'>
                <select
                  id='panel-edit-returned'
                  className='form-select form-select-solid'
                  onChange={(e) => setNalogVracen(e.target.value)}
                  value={nalogVracen}
                >
                  <option value=''>Odaberi</option>
                  <option value='1'>Da</option>
                  <option value='0'>Ne</option>
                </select>
              </div>
              <div className='col-12 col-sm-6 col-xl-12 col-xxl-6'>
                <ActionButton
                  text='Potvrdi vraćen'
                  onClick={onClickNalogVracen}
                  loading={loading}
                  disabled={!nalogVracen || !selectedCount}
                  variant='btn-light-primary'
                  iconPath='/media/icons/duotune/general/gen043.svg'
                />
              </div>
            </div>
            <div className='border-top pt-5'>
              <label htmlFor='panel-edit-archive' className='form-label fw-semibold'>
                Arhiviraj nalog
              </label>
              <div className='row g-3 align-items-end'>
                <div className='col-12 col-sm-6 col-xl-12 col-xxl-6'>
                  <select
                    id='panel-edit-archive'
                    className='form-select form-select-solid'
                    onChange={(e) => setArhivirajNalog(e.target.value)}
                    value={arhivirajNalog}
                  >
                    <option value=''>Odaberi</option>
                    <option value='1'>Da</option>
                    <option value='0'>Ne</option>
                  </select>
                </div>
                <div className='col-12 col-sm-6 col-xl-12 col-xxl-6'>
                  <ActionButton
                    text='Potvrdi arhivu'
                    onClick={onClickArhivirajNalog}
                    loading={loading}
                    disabled={!arhivirajNalog || !selectedCount}
                    variant='btn-light-primary'
                    iconPath='/media/icons/duotune/general/gen055.svg'
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='col-12 col-xl-4'>
          <div className='bg-light-primary rounded p-5 h-100 d-flex flex-column'>
            <div className='d-flex align-items-center gap-3 mb-5'>
              <i className='bi bi-file-earmark-plus text-primary fs-2' aria-hidden='true' />
              <h4 className='fs-5 fw-bold mb-0'>Upis naloga</h4>
            </div>
            <p className='text-gray-700 fs-7 mb-5'>
              Unesite broj FMK naloga koji želite upisati u proizvodnju.
            </p>
            <label htmlFor='panel-import-nalog' className='form-label fw-semibold'>
              Broj naloga
            </label>
            <input
              id='panel-import-nalog'
              type='text'
              className='form-control form-control-solid mb-5'
              placeholder='Npr. 001935'
              value={upisiNalog}
              onChange={(e) => setupisiNalog(e.target.value)}
            />
            <div className='mt-auto'>
              <ActionButton
                text='Snimi upis'
                onClick={handleUpisiNalog}
                loading={loading}
                disabled={!upisiNalog}
                variant='btn-primary'
                iconPath='/media/icons/duotune/general/gen064.svg'
              />
            </div>
          </div>
        </div>
      </div>
    </PanelListSection>
  )
}

export default PrroizodnjaIzmjena
